/**
 * 保存形 (SwipeClipParams) → プレビュー用レンダ入力・既定値・再編集の読み出し (image-generator 側だけの補助)。
 * docs/features/swipe-motion-clip-plan.md §4.1 / §5.5 / §8。
 *
 * プレビューは元画像をそのまま渡す ('preview' モード: image = 元画像、crop.height = H')。
 * 数値の規則 (整数寸法・停止位置・尺) は video-editor からコピーした geometry.ts / timeline.ts が正本で、ここでは組み合わせるだけ。
 */
import { buildRenderInputGeometry, buildSwipeGeometry, frameLayoutFor, isFrameAllowed, isTallEnough, screenRectFor } from './geometry';
import {
  SWIPE_COUNT_MAX,
  SWIPE_DEFAULTS,
  SWIPE_FPS,
  SwipeClipParamsSchema,
  type SwipeClipParams,
  type SwipeFeel,
  type SwipeFrame,
  type SwipeFramePosition,
  type SwipeOutput,
  type SwipeRect,
  type SwipeRenderInput,
} from './schema';
import { buildSwipeTimeline, type SwipeTimeline } from './timeline';

/** 出力の解像度 (素材パネルからのスタンドアロンで選べる。シーンからは 1080p 固定)。 */
export const SWIPE_RESOLUTIONS = ['720p', '1080p', '4k'] as const;
export type SwipeResolution = (typeof SWIPE_RESOLUTIONS)[number];
export const SWIPE_RESOLUTION_LABELS: Record<SwipeResolution, string> = { '720p': '720p', '1080p': '1080p', '4k': '4K' };
export const SWIPE_DEFAULT_RESOLUTION: SwipeResolution = '1080p';

/** アスペクト比 × 解像度 → 出力寸法 (backend/services/swipe_clip/params.py の SWIPE_OUTPUT_PRESETS と同値。BE が正本で保存時に検証する)。
 *  1080p は brand_cut_motion の MOTION_OUTPUT_DIMENSIONS と同じ。21:9 の 4K は幅 3840 に収める。 */
export const SWIPE_OUTPUT_PRESETS: Record<string, Record<SwipeResolution, { width: number; height: number }>> = {
  '16:9': { '720p': { width: 1280, height: 720 }, '1080p': { width: 1920, height: 1080 }, '4k': { width: 3840, height: 2160 } },
  '9:16': { '720p': { width: 720, height: 1280 }, '1080p': { width: 1080, height: 1920 }, '4k': { width: 2160, height: 3840 } },
  '1:1': { '720p': { width: 720, height: 720 }, '1080p': { width: 1080, height: 1080 }, '4k': { width: 2160, height: 2160 } },
  '4:3': { '720p': { width: 960, height: 720 }, '1080p': { width: 1440, height: 1080 }, '4k': { width: 2880, height: 2160 } },
  '3:4': { '720p': { width: 720, height: 960 }, '1080p': { width: 1080, height: 1440 }, '4k': { width: 2160, height: 2880 } },
  '21:9': { '720p': { width: 1680, height: 720 }, '1080p': { width: 2520, height: 1080 }, '4k': { width: 3840, height: 1646 } },
};

/** アスペクト比 → 1080p の出力寸法 (後方互換)。 */
export const SWIPE_OUTPUT_DIMENSIONS: Record<string, { width: number; height: number }> = Object.fromEntries(
  Object.entries(SWIPE_OUTPUT_PRESETS).map(([aspect, presets]) => [aspect, presets['1080p']]),
);
const DEFAULT_ASPECT = '16:9';

/** 手触りの表示名 (FeelPicker と再編集の表示が共用)。 */
export const SWIPE_FEEL_LABELS: Record<SwipeFeel, string> = {
  settle: '減速停止',
  bounce: '少し戻る',
  smooth: 'なめらか',
  linear: '一定速度',
};

/** 枠の表示名 (§11.1)。選べる枠は出力の縦横で決まる (geometry.ts の framesAllowedFor)。 */
export const SWIPE_FRAME_LABELS: Record<SwipeFrame, string> = {
  none: 'なし',
  phone: 'スマホ',
  pc: 'PC',
};

/**
 * 枠の位置を選べる組み合わせ (利用者指示 2026-10-13): 横長 (正方形を含む) のスマホは左右、縦長の PC は上下。
 * 縦長のスマホ (高さ 90% で余地が無い) と横長の PC (台座込みで幅 90%) は位置を出さず center 固定にする。
 */
export function isFramePositionSelectable(frame: SwipeFrame, output: { width: number; height: number }): boolean {
  const landscape = output.width >= output.height;
  return (landscape && frame === 'phone') || (!landscape && frame === 'pc');
}

/** 枠の位置の表示名。横長 (正方形を含む) は左 / 中央 / 右、縦長は上 / 中央 / 下 (geometry.ts の positionedOffset と同じ読み替え)。 */
export function framePositionLabelsFor(output: { width: number; height: number }): Record<SwipeFramePosition, string> {
  return output.width >= output.height ? { start: '左', center: '中央', end: '右' } : { start: '上', center: '中央', end: '下' };
}

export function swipeOutputFor(aspectRatio: string | null | undefined, resolution: SwipeResolution = SWIPE_DEFAULT_RESOLUTION): SwipeOutput {
  const aspect = aspectRatio && SWIPE_OUTPUT_PRESETS[aspectRatio] ? aspectRatio : DEFAULT_ASPECT;
  const presets = SWIPE_OUTPUT_PRESETS[aspect] ?? SWIPE_OUTPUT_PRESETS[DEFAULT_ASPECT]!;
  const dims = presets[resolution] ?? presets[SWIPE_DEFAULT_RESOLUTION];
  return { width: dims.width, height: dims.height, fps: SWIPE_FPS, aspectRatio: aspect };
}

/**
 * 元画像の幅に対する各解像度の拡大率 (幅合わせ: 画面幅 / 元画像の幅。元画像の方が広ければ 1)。
 * 拡大が大きい解像度は細部が増えないのに書き出しだけ重くなるので、モーダルは警告しきい値を超える選択肢を非活性にする
 * (ただし最小の解像度は常に選べる)。
 */
export function swipeUpscaleRatioFor(aspectRatio: string, resolution: SwipeResolution, sourceWidth: number, frame: SwipeFrame = 'none'): number {
  const dims = swipeOutputFor(aspectRatio, resolution);
  // 枠ありは画面 (筐体の内側) の幅が基準
  const screenWidth = frameLayoutFor(isFrameAllowed(frame, dims) ? frame : 'none', dims).screenRect.width;
  return sourceWidth > 0 ? Math.max(1, screenWidth / sourceWidth) : 1;
}

/** シーン尺 (fitScene の totalSec)。backend の planned_duration_sec と同じ規則 (既定 4 秒・1〜60 秒)。BE が保存時に決め直す。 */
export function plannedSceneDurationSec(scene: { video_duration_seconds?: number | null }): number {
  const value = typeof scene.video_duration_seconds === 'number' && Number.isFinite(scene.video_duration_seconds) ? scene.video_duration_seconds : 4;
  return Math.min(60, Math.max(1, value));
}

export interface SwipeSourceInfo {
  materialId: string;
  folder: 'temporary' | 'shared';
  width: number;
  height: number;
}

/** 新規作成時の既定の保存形。 */
export function buildDefaultSwipeParams(source: SwipeSourceInfo, output: SwipeOutput, backgroundColor: string | null | undefined): SwipeClipParams {
  return {
    version: 1,
    source: { ...source },
    output,
    range: { ...SWIPE_DEFAULTS.range },
    swipes: { ...SWIPE_DEFAULTS.swipes },
    timing: { ...SWIPE_DEFAULTS.timing },
    look: { ...SWIPE_DEFAULTS.look, backgroundColor: normalizeHexColor(backgroundColor) ?? SWIPE_DEFAULTS.look.backgroundColor },
    cropToEnd: { ...SWIPE_DEFAULTS.cropToEnd },
  };
}

export function normalizeHexColor(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  return /^#[0-9a-fA-F]{6}$/.test(text) ? text.toUpperCase() : null;
}

/**
 * 選択中の動画素材の generation_metadata から保存形を読む (再編集・§4.1 / §7.3)。
 * 元画像が今の画像と違えば source を差し替え、寸法が違えば range を既定に戻す (`sourceReplaced` で知らせる)。
 * 出力の縦横に合わない枠 (シナリオの比率が変わった等) は「なし」に戻す。
 */
export function paramsFromMaterialMetadata(
  metadata: Record<string, unknown> | null | undefined,
  current: SwipeSourceInfo,
  output: SwipeOutput,
): { params: SwipeClipParams; sourceReplaced: boolean } | null {
  if (!metadata || metadata.engine !== 'swipe_clip') return null;
  const parsed = SwipeClipParamsSchema.safeParse(metadata.swipe_clip);
  if (!parsed.success) return null;
  const saved = parsed.data;
  const sameSource = saved.source.materialId === current.materialId;
  const sameDims = saved.source.width === current.width && saved.source.height === current.height;
  const params: SwipeClipParams = {
    ...saved,
    source: { ...current },
    output,
    range: sameDims ? saved.range : { ...SWIPE_DEFAULTS.range },
    look: isFrameAllowed(saved.look.frame, output) ? saved.look : { ...saved.look, frame: 'none' },
  };
  return { params, sourceReplaced: !sameSource };
}

/** プレビュー用レンダ入力 (元画像をそのまま渡す)。スクリーン矩形は look.frame の配置 (枠無しは出力全面)。 */
export function buildPreviewRenderInput(params: SwipeClipParams, screenRect: SwipeRect = screenRectFor(params)): SwipeRenderInput {
  return {
    ...buildRenderInputGeometry(params, screenRect, 'preview'),
    output: params.output,
    swipes: params.swipes,
    timing: params.timing,
    look: params.look,
  };
}

export interface SwipePreview {
  input: SwipeRenderInput;
  timeline: SwipeTimeline;
  viewportPct: number;
  upscaleRatio: number;
}

/** 保存形からプレビューに要るもの一式。尺が作れないときは SwipeTimelineError が投げられる。 */
export function buildSwipePreview(params: SwipeClipParams): SwipePreview {
  const screenRect = screenRectFor(params);
  const geometry = buildSwipeGeometry(params, screenRect);
  const input = buildPreviewRenderInput(params, screenRect);
  const timeline = buildSwipeTimeline({
    s0: geometry.s0,
    sEndEdge: geometry.sEndEdge,
    viewportPct: geometry.viewportPct,
    swipes: params.swipes,
    timing: params.timing,
    fps: params.output.fps,
  });
  return { input, timeline, viewportPct: geometry.viewportPct, upscaleRatio: geometry.upscaleRatio };
}

/**
 * 新規作成時のスワイプ回数の初期値 (§4.2)。画像の長さ (スクロールできる距離) と 1 回の移動量から「必要な回数」を、
 * シーン尺と各区間の秒数から「尺に入る回数」を出し、小さい方を 1〜SWIPE_COUNT_MAX に収める。
 * 「下端まで」モード (amountPct 無し) では 1 回の移動量を画面の 80% とみなす。
 */
export function suggestSwipeCount(params: SwipeClipParams, totalSec: number): number {
  const geometry = buildSwipeGeometry(params, screenRectFor(params));
  const distance = geometry.sEndEdge - geometry.s0 - geometry.viewportPct; // 仮想画像の高さ % (画面 1 枚分を除く)
  if (!(distance > 0)) return 1;
  const amountPct = params.swipes.amountPct ?? 80;
  const step = (geometry.viewportPct * amountPct) / 100;
  const needed = Math.ceil(distance / step - 1e-9);
  const { holdStartSec, holdEndSec, swipeSec, gapSec } = params.timing;
  const perSwipe = swipeSec + gapSec;
  const byTime = perSwipe > 0 ? Math.floor((totalSec - holdStartSec - holdEndSec + gapSec) / perSwipe) : SWIPE_COUNT_MAX;
  return Math.max(1, Math.min(SWIPE_COUNT_MAX, needed, Math.max(1, byTime)));
}

export function isSwipeSourceTall(source: { width: number; height: number }, output: SwipeOutput): boolean {
  return isTallEnough(source, output);
}
